// Prototype-only catalog, project, and photography content.
// Confirmed business information lives in siteConfig.js.

const U = (id, w = 1400) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=80`;

export const IMAGES = {
  // Hero — hands knitting with colorful yarn (verified)
  hero: U("photo-1632649027900-389e810204e6", 1600),

  // Yarn balls / skeins (verified from Unsplash yarn-skein search)
  skeinsWarm: U("photo-1611882565804-465477102f3d"),
  skeinsBrown: U("photo-1613152768223-c17cb6e85bc2"),
  skeinsPile: U("photo-1641060889144-1cc91e6871ce"),
  skeinsGroup: U("photo-1668072587819-7b393944b426"),
  skeinsGroup2: U("photo-1668072587859-f0f30c8fa938"),
  skeinsColorful: U("photo-1668072587992-7c0d529d5363"),
  skeinsNeutral: U("photo-1513890333407-6f85205e8ef2"),
  skeinsBlueRed: U("photo-1595341595379-cf1cb694ea1f"),
  skeinsRopes: U("photo-1532607792384-110a601b26df"),
  ballOfYarn: U("photo-1517490970599-197965fbcef4"),
  yarnThreads: U("photo-1574883140236-2e2cb0835792"),
  yarnOnTable: U("photo-1719859065270-e6f231f38dd8"),
  yarnOrange: U("photo-1584992236310-6edddc08acff"),

  // Knitting in progress (verified from earlier screenshots)
  cozyProject: U("photo-1541944743827-e04aa6427c33"),
  handKnitStack: U("photo-1601379327928-bedfaf9da2d0"),

  // Texture / atmosphere
  yarnTexture: U("photo-1781995417415-624761984908"),
  desertTexture: U("photo-1596625820723-f0f481ff80be"),

  // Provisional store atmosphere image, used until real photography is available.
  storeInterior: U("photo-1719859065270-e6f231f38dd8", 1800),

  // Gift wrapping (verified in earlier screenshot)
  wrappedGift: U("photo-1512909006721-3d6018887383"),
};

// Aliases used across pages
IMAGES.skeins = IMAGES.skeinsWarm;
IMAGES.sweater = IMAGES.handKnitStack;
IMAGES.yarnPile = IMAGES.skeinsGroup;
IMAGES.woolBasket = IMAGES.yarnThreads;
IMAGES.needles = IMAGES.ballOfYarn;
IMAGES.crochet = IMAGES.skeinsGroup2;
IMAGES.blanket = IMAGES.yarnOnTable;
IMAGES.scarf = IMAGES.skeinsBrown;
IMAGES.giftBundle = IMAGES.skeinsColorful;
IMAGES.handsWorking = IMAGES.cozyProject;
IMAGES.storeShelf = IMAGES.skeinsNeutral;
IMAGES.storeDetail = IMAGES.yarnTexture;
IMAGES.store = IMAGES.storeInterior;
IMAGES.product1 = IMAGES.skeinsBrown;
IMAGES.product2 = IMAGES.skeinsPile;
IMAGES.handKnit = IMAGES.handKnitStack;

export const WHAT_WE_CARRY = [
  {
    id: "yarn-fiber",
    title: "Yarn & Fiber",
    line: "Color, texture, and weight for projects of every size.",
    image: IMAGES.skeinsWarm,
    href: "/shop",
  },
  {
    id: "tools-notions",
    title: "Tools & Notions",
    line: "Needles, hooks, and useful extras for the work at hand.",
    image: IMAGES.ballOfYarn,
    href: "/shop",
  },
  {
    id: "gifts",
    title: "Gifts for Makers",
    line: "Thoughtful options for people who love to make.",
    image: IMAGES.wrappedGift,
    href: "/gifts",
  },
];

export const PROJECT_CARDS = [
  {
    id: "desert-wrap",
    title: "The Desert Wrap",
    pair: "Pairs with heirloom merino.",
    line: "A weekend meditation, worn all winter.",
    image: IMAGES.skeinsGroup2,
  },
  {
    id: "hearth-blanket",
    title: "Hearth Blanket",
    pair: "Pairs with chunky wool.",
    line: "Slow rows, deep quiet.",
    image: IMAGES.yarnOnTable,
  },
  {
    id: "everyday-pullover",
    title: "Everyday Pullover",
    pair: "Pairs with soft alpaca blends.",
    image: IMAGES.handKnitStack,
    line: "One good sweater, worn for years.",
  },
  {
    id: "market-tote",
    title: "The Market Tote",
    pair: "Pairs with hand-dyed cotton.",
    line: "An afternoon project, endlessly useful.",
    image: IMAGES.skeinsColorful,
  },
];

export const GIFT_PATHWAYS = [
  {
    id: "for-the-new-maker",
    title: "For the New Maker",
    copy: "A few approachable ways to support their first project.",
    image: IMAGES.ballOfYarn,
  },
  {
    id: "for-the-devoted",
    title: "For the Devoted",
    copy: "Thoughtful materials for someone with a project in progress.",
    image: IMAGES.skeinsWarm,
  },
  {
    id: "not-sure",
    title: "Not Sure Yet",
    copy: "Browse the current online selection together.",
    image: IMAGES.wrappedGift,
  },
];

export const PRODUCTS = [
  {
    id: "alpaca-heirloom",
    name: "Heirloom Alpaca",
    price: "See Shopify",
    tag: "Illustrative item",
    color: "Warm bone",
    fiber: "70% Alpaca · 30% Merino",
    weight: "Worsted",
    yardage: "220 yd / 100 g",
    gauge: "18 sts × 24 rows / 4\"",
    care: "Hand wash · Dry flat",
    image: IMAGES.skeinsBrown,
    gallery: [IMAGES.skeinsBrown, IMAGES.yarnTexture, IMAGES.skeinsWarm, IMAGES.cozyProject],
    descriptor: "A soft, breath-warm worsted for garments meant to be kept.",
    warm:
      "This is the skein we reach for when we want a project to feel considered. Long staples, gentle halo, and a hand-feel that quietly explains itself the first time you cast on.",
    love: [
      { label: "Ideal for", value: "Wraps, cardigans, soft garments" },
      { label: "Beginner-friendly", value: "Yes — forgiving stitch definition" },
      { label: "Texture feel", value: "Soft, balanced, gently plush" },
      { label: "Pairs well with", value: "Desert Hues collection · Everyday Pullover" },
    ],
  },
  {
    id: "merino-quiet",
    name: "Quiet Merino",
    price: "See Shopify",
    tag: "Illustrative item",
    color: "Oat",
    fiber: "100% Merino Wool",
    weight: "DK",
    yardage: "260 yd / 100 g",
    gauge: "22 sts × 28 rows / 4\"",
    care: "Hand wash cool",
    image: IMAGES.skeinsNeutral,
    gallery: [IMAGES.skeinsNeutral, IMAGES.skeinsGroup, IMAGES.yarnThreads],
    descriptor: "A steady DK with clean stitch definition and honest hand.",
    warm:
      "For pieces where the stitch pattern is the point. Round, springy, and quietly opinionated about tension.",
    love: [
      { label: "Ideal for", value: "Textured pullovers, hats, mitts" },
      { label: "Beginner-friendly", value: "Maybe — rewards even tension" },
      { label: "Texture feel", value: "Crisp, springy, alive" },
      { label: "Pairs well with", value: "Quiet Neutrals · Hearth Blanket" },
    ],
  },
  {
    id: "silk-blend",
    name: "Desert Silk Blend",
    price: "See Shopify",
    tag: "Illustrative item",
    color: "Clay",
    fiber: "50% Silk · 50% Merino",
    weight: "Fingering",
    yardage: "435 yd / 100 g",
    gauge: "28 sts × 36 rows / 4\"",
    care: "Hand wash · Lay flat",
    image: IMAGES.yarnTexture,
    gallery: [IMAGES.yarnTexture, IMAGES.desertTexture, IMAGES.skeinsGroup2],
    descriptor: "A luminous fingering with the drape of memory.",
    warm:
      "Held single for shawls, doubled for garments — either way it moves like water and lights up like a slow afternoon.",
    love: [
      { label: "Ideal for", value: "Shawls, lace, delicate accessories" },
      { label: "Beginner-friendly", value: "Not first choice — slippery hand" },
      { label: "Texture feel", value: "Fluid, luminous, cool" },
      { label: "Pairs well with", value: "Desert Hues · The Desert Wrap" },
    ],
  },
  {
    id: "chunky-hearth",
    name: "Hearth Chunky",
    price: "See Shopify",
    tag: "Illustrative item",
    color: "Forest",
    fiber: "80% Wool · 20% Alpaca",
    weight: "Bulky",
    yardage: "108 yd / 100 g",
    gauge: "12 sts × 16 rows / 4\"",
    care: "Hand wash · Dry flat",
    image: IMAGES.yarnOnTable,
    gallery: [IMAGES.yarnOnTable, IMAGES.handKnitStack, IMAGES.skeinsPile],
    descriptor: "A grounded bulky for the projects worth finishing in one sitting.",
    warm:
      "Rows accumulate quickly, which is a small mercy when the days get short. Substantial, honest, and slightly wild.",
    love: [
      { label: "Ideal for", value: "Blankets, chunky scarves, statement hats" },
      { label: "Beginner-friendly", value: "Yes — big stitches, quick wins" },
      { label: "Texture feel", value: "Substantial, warm, honest" },
      { label: "Pairs well with", value: "Hearth Blanket · Everyday Pullover" },
    ],
  },
];
